import { createFileRoute } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import { useCurrentUser, isSuperAdminRole } from '../lib/auth'
import { formatDateLong, formatDateTimeShort } from '../lib/dates'
import { formatNumber, formatWeight } from '../lib/format'

export const Route = createFileRoute('/admin/othekkt-efni')({
  component: UnknownMaterialsPage,
})

function UnknownMaterialsPage() {
  const { data: me, isLoading: meLoading } = useCurrentUser()
  const qc = useQueryClient()
  const isSuper = isSuperAdminRole(me)

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'unknown-materials'],
    queryFn: () => api.admin.unknownMaterials.get(),
    enabled: isSuper,
  })

  const refresh = useMutation({
    mutationFn: () => api.admin.unknownMaterials.refresh(),
    onSuccess: (res) => qc.setQueryData(['admin', 'unknown-materials'], res),
  })

  const report = data?.report ?? refresh.data?.report ?? null

  if (meLoading) return <Shell><p className="text-[var(--muted)]">Hleð…</p></Shell>
  if (!isSuper) return (
    <Shell>
      <div className="rounded-xl bg-amber-50 border border-amber-200 p-6 text-amber-900">
        <h2 className="font-semibold">Enginn aðgangur</h2>
        <p className="mt-1 text-sm">Aðeins superadmin sér þessa skýrslu.</p>
      </div>
    </Shell>
  )

  return (
    <Shell>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <h1 className="font-grotesk font-bold text-[28px] leading-tight text-[var(--fg)]">Óþekkt vörunúmer</h1>
          <p className="mt-1 text-sm text-[var(--muted)] max-w-[760px]">
            Vörunúmer í SVR2-gögnunum sem vantar í efnisskrá Minna síðna. Þau reiknast sem „Óþekkt efni“ í urðun þar til
            þau eru skráð, sem lækkar endurvinnsluhlutfall og hækkar kolefnisspor viðskiptavinanna.
            {report && <> Skoðað: {formatNumber(report.cachedMonths)} mánuðir hjá {formatNumber(report.customers)} viðskiptavinum í SVR2-skyndiminninu.</>}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {report && <span className="text-xs text-[var(--faint)]">Síðast reiknað: {formatDateTimeShort(report.generatedAt)}</span>}
          <button
            onClick={() => refresh.mutate()}
            disabled={refresh.isPending}
            className="rounded-lg bg-[var(--brand)] px-4 py-2 text-sm font-semibold text-[var(--on-brand)] hover:opacity-90 disabled:opacity-50"
          >
            {refresh.isPending ? 'Reikna…' : 'Uppfæra núna'}
          </button>
        </div>
      </div>

      {isLoading && <p className="text-[var(--muted)]">Hleð…</p>}

      {!isLoading && !report && (
        <div className="rounded-xl border border-[var(--border)] bg-[var(--surface)] p-6 text-sm text-[var(--muted)]">
          Engin skýrsla reiknuð enn. Smelltu á „Uppfæra núna“ til að reikna hana.
        </div>
      )}

      {report && report.materials.length === 0 && (
        <div className="rounded-xl border border-[var(--border)] bg-[var(--surface)] p-6 text-sm text-[var(--fg)]">
          Ekkert óþekkt vörunúmer. Öll vörunúmer í SVR2-gögnunum eru í efnisskránni.
        </div>
      )}

      {report && report.materials.length > 0 && (
        <div className="rounded-xl border border-[var(--border)] bg-[var(--surface)] overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-[var(--faint)] border-b border-[var(--border)]">
                <th className="px-4 py-2 font-medium">Vörunúmer</th>
                <th className="px-3 py-2 font-medium">Heiti í SVR2</th>
                <th className="px-3 py-2 font-medium">Yfirflokkur í SVR2</th>
                <th className="px-3 py-2 font-medium text-right">Magn</th>
                <th className="px-3 py-2 font-medium text-right">Losanir</th>
                <th className="px-3 py-2 font-medium">Tímabil</th>
                <th className="px-3 py-2 font-medium">Viðskiptavinir</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--border)]">
              {report.materials.map((m) => (
                <tr key={m.materialId} className="align-top hover:bg-[var(--surface-2)]">
                  <td className="px-4 py-2 font-semibold text-[var(--fg)] tabular-nums">{m.materialId}</td>
                  <td className="px-3 py-2 text-[var(--fg)]">{m.materialName || '—'}</td>
                  <td className="px-3 py-2 text-[var(--muted)]">{m.materialGroup ?? '—'}</td>
                  <td className="px-3 py-2 text-right font-semibold text-[var(--fg)] tabular-nums whitespace-nowrap">{formatWeight(m.kg)}</td>
                  <td className="px-3 py-2 text-right text-[var(--muted)] tabular-nums">{formatNumber(m.collections)}</td>
                  <td className="px-3 py-2 text-[var(--muted)] whitespace-nowrap">
                    {m.firstSeen ? `${formatDateLong(m.firstSeen)} – ${formatDateLong(m.lastSeen)}` : '—'}
                  </td>
                  <td className="px-3 py-2 text-[var(--muted)]">
                    {m.companies.map((c) => (
                      <div key={c.kennitala}>
                        <span className="text-[var(--fg)]">{c.name}</span> · {formatWeight(c.kg)}
                      </div>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Shell>
  )
}

function Shell({ children }: { children: React.ReactNode }) {
  return <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-8">{children}</div>
}
